import React, { useState, useCallback } from 'react';
import { View, StyleSheet, ScrollView, Alert, Pressable } from 'react-native';
import { Text, Card, IconButton, Switch, ActivityIndicator, Chip } from 'react-native-paper';
import { useRouter } from 'expo-router';
import ScreenMargin from '@/components/ScreenMargin';
import Form from '@/components/Form/Form';
import TextInput from '@/components/Form/TextInput';
import { Image } from 'expo-image';
import { useForm } from 'react-hook-form';
import { useToast } from 'react-native-toast-notifications';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { Color as ThemeColors, Border, Padding, Color } from '@/GlobalStyles';
import { useGetHelpTextsQuery, useDeleteHelpTextMutation } from '@/data/data';
import { getFieldLabel } from '@/utils/helpTextsUtils';
import { useRefetchOnFocus } from '@/hooks/useRefetchOnFocus';
import DataTable from '@/components/DataTable/DataTable';

export default function AideContextuelleScreen() {
  const router = useRouter();
  const toast = useToast();
  const { t } = useTranslation();

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showCustomOnly, setShowCustomOnly] = useState(false);

  // Form pour la recherche
  const searchForm = useForm();
  const searchQuery = searchForm.watch('search');

  const { data, loading, error, refetch } = useGetHelpTextsQuery();

  // Refetch when the screen gains focus
  useRefetchOnFocus(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  const [deleteHelpText] = useDeleteHelpTextMutation({
    fetchPolicy: 'network-only',
    onCompleted: () => {
      toast.show(t('Personnalisation supprimée'), { type: 'success' });
      refetch();
    },
    onError: (error) => {
      console.error('Error deleting help text:', error);
      toast.show(t('Erreur lors de la suppression'), { type: 'error' });
    },
  });

  // Grouper les textes par champ
  const groupedTexts = React.useMemo(() => {
    if (!data?.helpTexts) return {};

    const grouped: Record<string, any[]> = {};
    data.helpTexts.forEach((text: any) => {
      if (!grouped[text.fieldName]) {
        grouped[text.fieldName] = [];
      }
      grouped[text.fieldName].push(text);
    });

    return grouped;
  }, [data]);

  // Extraire les catégories uniques des données
  const categories = React.useMemo(() => {
    if (!data?.helpTexts) return [];

    const uniqueCategories = new Set<string>();
    data.helpTexts.forEach((text: any) => {
      if (text.category) {
        uniqueCategories.add(text.category);
      }
    });

    return Array.from(uniqueCategories).sort();
  }, [data]);

  // Filtrer par catégorie et recherche - retourner des objets avec fieldName et texts
  const filteredFields = React.useMemo(() => {
    let fields = Object.keys(groupedTexts);

    // Filtre par recherche
    if (searchQuery) {
      fields = fields.filter((field) => {
        const texts = groupedTexts[field];
        const fieldLabel = getFieldLabel(field, t);
        return (
          field.toLowerCase().includes(searchQuery.toLowerCase()) ||
          fieldLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
          texts.some(
            (t: any) =>
              (t.textFr && t.textFr.toLowerCase().includes(searchQuery.toLowerCase())) ||
              (t.textEn && t.textEn.toLowerCase().includes(searchQuery.toLowerCase()))
          )
        );
      });
    }

    if (selectedCategory) {
      fields = fields.filter((field) => {
        const texts = groupedTexts[field];
        return texts.some((t: any) => t.category === selectedCategory);
      });
    }

    if (showCustomOnly) {
      fields = fields.filter((field) => groupedTexts[field].some((t: any) => t.chirurgienId));
    }

    return fields.map((fieldName) => ({
      id: fieldName,
      fieldName,
      texts: groupedTexts[fieldName],
      defaultText: groupedTexts[fieldName].find((t: any) => t.isDefault),
      customText: groupedTexts[fieldName].find((t: any) => !t.isDefault),
    }));
  }, [groupedTexts, selectedCategory, showCustomOnly, searchQuery]);

  const handleEdit = (fieldName: string) => {
    router.push(`/parametres/aide-contextuelle/${fieldName}`);
  };

  const handleDelete = (id: string) => {
    Alert.alert(t('Confirmation'), t('Voulez-vous vraiment supprimer cette personnalisation ?'), [
      { text: t('Annuler'), style: 'cancel' },
      {
        text: t('Supprimer'),
        style: 'destructive',
        onPress: () => deleteHelpText({ variables: { id } }),
      },
    ]);
  };

  // Fonction de rendu pour les cartes
  const renderHelpTextCard = (item: any) => {
    const { fieldName, defaultText, customText } = item;

    return (
      <View style={styles.cardContainer}>
        <Pressable onPress={() => handleEdit(fieldName)} style={styles.card}>
          <View style={styles.cardContent}>
            <View style={styles.cardHeader}>
              <View style={styles.cardInfo}>
                <Text style={styles.cardTitle}>{getFieldLabel(fieldName, t)}</Text>
                <Text style={styles.cardSubtitle}>
                  {t('Champ')}: {fieldName}
                </Text>
              </View>

              {/* Actions */}
              <View style={styles.cardActions}>
                {customText && (
                  <IconButton
                    icon="delete"
                    size={20}
                    onPress={() => handleDelete(customText.id)}
                    iconColor={ThemeColors.error}
                    style={{ margin: 0 }}
                  />
                )}
                <IconButton
                  icon="pencil"
                  size={20}
                  onPress={() => handleEdit(fieldName)}
                  iconColor={ThemeColors.MainBleu}
                  style={{ margin: 0 }}
                />
              </View>
            </View>

            {/* Textes d'aide */}
            {customText && (
              <View style={styles.textContainer}>
                <Chip icon="account" style={styles.customChip}>
                  {t('Personnalisé')}
                </Chip>
                <Text style={styles.helpText}>
                  {i18n.language === 'en' && customText.textEn
                    ? customText.textEn
                    : customText.textFr}
                </Text>
              </View>
            )}
            {defaultText && (
              <View style={[styles.textContainer, customText && styles.defaultTextMuted]}>
                <Chip icon="earth" style={styles.defaultChip}>
                  {t('Par défaut')}
                </Chip>
                <Text style={[styles.helpText, customText && styles.mutedText]}>
                  {i18n.language === 'en' && defaultText.textEn
                    ? defaultText.textEn
                    : defaultText.textFr}
                </Text>
              </View>
            )}
          </View>
        </Pressable>
      </View>
    );
  };

  return (
    <ScreenMargin>
      <DataTable
        forceListType="card"
        forceNumberOfColumns={1}
        renderCard={renderHelpTextCard}
        title={t('Aide contextuelle')}
        loading={loading}
        data={filteredFields}
        dataTotalCount={filteredFields.length}
        keyExtractor={(item) => `help_${item.id}`}
        headers={[]}
        onItemPress={(item) => handleEdit(item.fieldName)}
        emptyStateText={t("Aucun texte d'aide trouvé")}
        subHeaderComponent={
          <>
            <Text style={styles.subtitle}>
              {t("Gérez les textes d'aide qui s'affichent dans les formulaires")}
            </Text>

            <Form {...searchForm} defaultValues={{ search: '' }}>
              <View
                style={{ flexDirection: 'row', marginTop: 12, justifyContent: 'space-between' }}
              >
                <TextInput
                  name={'search'}
                  label={t('Recherche')}
                  containerStyle={{ marginTop: 0, flex: 1 }}
                />
                <Pressable style={styles.iconContainer} onPress={() => refetch()}>
                  <Image
                    style={styles.searchIcon}
                    contentFit="cover"
                    source={require('@assets/icones/loupe.png')}
                  />
                </Pressable>
              </View>
            </Form>

            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              style={styles.filters}
              contentContainerStyle={{ gap: 8 }}
            >
              <Chip
                selected={!selectedCategory}
                onPress={() => setSelectedCategory(null)}
                mode={'outlined'}
                textStyle={{
                  color: !selectedCategory ? Color.black : Color.colorGray_150,
                }}
              >
                {t('Tous')}
              </Chip>
              {categories.map((category) => (
                <Chip
                  key={category}
                  selected={selectedCategory === category}
                  onPress={() => setSelectedCategory(category)}
                  mode={'outlined'}
                  textStyle={{
                    color: selectedCategory === category ? Color.black : Color.colorGray_150,
                  }}
                >
                  {t(category)}
                </Chip>
              ))}
            </ScrollView>

            <View style={styles.switchContainer}>
              <Text>{t('Afficher uniquement mes personnalisations')}</Text>
              <Switch
                value={showCustomOnly}
                onValueChange={setShowCustomOnly}
                color={ThemeColors.MainBleu}
              />
            </View>
          </>
        }
      />
    </ScreenMargin>
  );
}

const styles = StyleSheet.create({
  subtitle: {
    fontSize: 14,
    color: ThemeColors.MainGris,
    lineHeight: 20,
    marginBottom: 12,
  },
  searchIcon: {
    height: 22,
    width: 22,
    opacity: 0.3,
  },
  iconContainer: {
    padding: Padding.p_mini,
    height: 48,
    width: 48,
    backgroundColor: ThemeColors.BlancCasse,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: Border.br_11xl,
    flexDirection: 'row',
    marginLeft: 12,
  },
  filters: {
    marginTop: 12,
    marginBottom: 12,
    maxHeight: 34,
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 16,
    backgroundColor: ThemeColors.BlancCasse,
    borderRadius: 12,
    marginBottom: 12,
  },
  // Styles pour les cartes
  cardContainer: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  card: {
    backgroundColor: ThemeColors.blanc,
    borderRadius: Border.br_xs,
    borderWidth: 1,
    borderColor: ThemeColors.colorGray_100,
    overflow: 'hidden',
  },
  cardContent: {
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  cardInfo: {
    flex: 1,
    marginRight: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: ThemeColors.MainNoir,
  },
  cardSubtitle: {
    fontSize: 12,
    color: ThemeColors.MainGris,
    marginTop: 2,
  },
  cardActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  textContainer: {
    marginVertical: 8,
  },
  customChip: {
    alignSelf: 'flex-start',
    marginBottom: 8,
    backgroundColor: ThemeColors.MainBleu + '15',
  },
  defaultChip: {
    alignSelf: 'flex-start',
    marginBottom: 8,
    backgroundColor: ThemeColors.BlancCasse,
  },
  helpText: {
    fontSize: 14,
    lineHeight: 22,
    color: ThemeColors.MainNoir,
  },
  mutedText: {
    opacity: 0.5,
  },
  defaultTextMuted: {
    opacity: 0.6,
  },
});
